import { Link, useNavigate } from "react-router-dom";
import { GCP_DATABASES, GCP_SIZES, type GcpPricingEstimate } from "@mycompbox/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useGcpPricing, useStartGcpSetup } from "@/hooks/queries/use-cloud";
import type { GcpPricingDatabase, GcpSize } from "@/lib/api/cloud";
import { gcpWizardPath, useGcpWizard } from "./wizard-state";

const DATABASE_LABELS: Record<GcpPricingDatabase, string> = {
  none: "No database",
  supabase: "Supabase",
  cloudsql: "Cloud SQL (Google Cloud)",
};
const SIZE_LABELS: Record<GcpSize, string> = { starter: "Starter", team: "Team", production: "Production" };

const usd = (n: number): string => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const range = (r: { low: number; high: number }): string => (r.low === r.high ? usd(r.low) : `${usd(r.low)} – ${usd(r.high)}`);

/** The cost table. Every number and assumption comes from the pricing API; nothing is priced here. */
export function CostTable({ estimate }: { estimate: GcpPricingEstimate }) {
  return (
    <TooltipProvider>
      <table className="w-full text-sm">
        <caption className="sr-only">Estimated monthly cost</caption>
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <th className="py-1 font-normal">Item</th>
            <th className="py-1 text-right font-normal">Per month</th>
          </tr>
        </thead>
        <tbody>
          {estimate.items.map((item) => (
            <tr key={item.id} className="border-t border-white/[0.06]">
              <td className="py-1.5">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="cursor-help underline decoration-dotted underline-offset-2">
                      {item.label}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <ul className="list-disc space-y-1 pl-4">
                      {item.assumptions.map((a) => (
                        <li key={a}>{a}</li>
                      ))}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              </td>
              <td className="py-1.5 text-right tabular-nums">{range(item)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t font-medium">
            <td className="py-1.5">Total</td>
            <td className="py-1.5 text-right tabular-nums">{range(estimate.total)}</td>
          </tr>
        </tfoot>
      </table>
      <p className="mt-2 text-xs text-muted-foreground">
        Live Google Cloud prices as of {new Date(estimate.asOf).toLocaleDateString()}. Hover an item to see what it assumes.
      </p>
      {estimate.assumptions.length > 0 && (
        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
          {estimate.assumptions.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      )}
    </TooltipProvider>
  );
}

/** Step 4: database choice, size, cost with assumptions, and the monthly budget (defaults to $25). */
export function PlanStep() {
  const { state, update } = useGcpWizard();
  const navigate = useNavigate();
  const pricing = useGcpPricing({ region: state.region, database: state.database, size: state.size });
  const start = useStartGcpSetup();

  if (!state.session) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">Sign in with Google first, then review the plan.</p>
        <Button asChild>
          <Link to={gcpWizardPath("sign-in")}>Sign in with Google</Link>
        </Button>
      </div>
    );
  }

  const budgetOk = Number.isInteger(state.budgetUsd) && state.budgetUsd >= 1;
  const build = () => {
    if (!state.session) return;
    start.mutate(
      {
        session: state.session,
        projectId: state.projectId,
        region: state.region,
        ...(state.billingAccountId ? { billingAccountId: state.billingAccountId } : {}),
        budgetUsd: state.budgetUsd,
      },
      { onSuccess: () => navigate(gcpWizardPath("build")) }
    );
  };

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold">Review the plan and cost</h2>
      <p className="text-sm text-muted-foreground">
        {state.projectMode === "new" ? "New project" : "Project"} <code>{state.projectId}</code> in {state.region}.
      </p>

      <div className="grid max-w-md gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="gcp-database">Database</Label>
          <Select value={state.database} onValueChange={(database) => update({ database: database as GcpPricingDatabase })}>
            <SelectTrigger id="gcp-database">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GCP_DATABASES.map((d) => (
                <SelectItem key={d} value={d}>
                  {DATABASE_LABELS[d]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="gcp-size">Size</Label>
          <Select value={state.size} onValueChange={(size) => update({ size: size as GcpSize })}>
            <SelectTrigger id="gcp-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GCP_SIZES.map((s) => (
                <SelectItem key={s} value={s}>
                  {SIZE_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {state.database === "supabase" && (
        <p className="text-sm text-muted-foreground">
          Supabase is connected separately: after this setup, add its connection string in each project's Database settings.{" "}
          <a className="underline" href="https://supabase.com/dashboard/new" target="_blank" rel="noreferrer">
            Open Supabase
          </a>
        </p>
      )}

      <div>
        {pricing.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading live Google Cloud prices…</p>
        ) : pricing.isError || !pricing.data ? (
          <p role="alert" className="text-sm text-destructive">
            Live Google Cloud prices aren't available right now, so there's no estimate to show. Try again in a few minutes.
          </p>
        ) : (
          <CostTable estimate={pricing.data} />
        )}
      </div>

      <div className="max-w-xs space-y-1.5">
        <Label htmlFor="gcp-budget">Monthly budget (USD)</Label>
        <Input
          id="gcp-budget"
          type="number"
          min={1}
          step={1}
          value={Number.isNaN(state.budgetUsd) ? "" : state.budgetUsd}
          onChange={(e) => update({ budgetUsd: e.target.value === "" ? Number.NaN : Number(e.target.value) })}
        />
        <p className="text-xs text-muted-foreground">
          CompBox warns you at 80% and 100% of this. {state.billingAccountId ? "" : "With no billing account, no budget is created."}
        </p>
      </div>

      {start.isError && (
        <p role="alert" className="text-sm text-destructive">
          {start.error instanceof Error ? start.error.message : "Couldn't start the setup."}
        </p>
      )}

      <div className="flex gap-2">
        <Button disabled={!budgetOk || start.isPending || !state.projectId} onClick={build}>
          {start.isPending ? "Starting…" : "Build it"}
        </Button>
        <Button asChild variant="ghost">
          <Link to={gcpWizardPath("project")}>Back</Link>
        </Button>
      </div>
    </div>
  );
}
